import {
  ArrowLeftRight,
  Beaker,
  Book,
  Briefcase,
  Building2,
  Check,
  ChevronDown,
  ExternalLink,
  FileText,
  Globe,
  Home,
  List,
  LogOut,
  Megaphone,
  Monitor,
  Moon,
  Search,
  Sun,
  User as UserIcon,
  Users,
} from "lucide-react";
import * as React from "react";
import { Link, useLocation } from "react-router";
import { AppSparkle } from "@/components/icons";
import { useTheme } from "@/components/theme-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/hooks/auth/use-auth";
import { usePermissions } from "@/hooks/auth/use-permissions";
import { useCompanySwitcher } from "@/hooks/use-company-switcher";
import {
  useCustomChannelsRulesManagementEnabled,
  useCustomChannelsVisibility,
  useDeepResearchEnabled,
  useMiniAppsVisibility,
} from "@/hooks/use-feature-flags";
import { cn } from "@/lib/utils";
import type { User as UserType } from "@/types/auth";

// Add type definitions for navigation items
interface NavSubItem {
  title: string;
  url: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface NavItem {
  title: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  directLink?: string; // Optional direct link property
  items: NavSubItem[];
}

// Updated navigation configuration with nested items
const NAV_CONFIG: {
  navMain: NavItem[];
} = {
  navMain: [
    {
      title: "Home",
      icon: Home,
      directLink: "/home",
      items: [
        {
          title: "Home",
          url: "/home",
          icon: Home,
        },
      ],
    },
    {
      title: "Apps",
      icon: AppSparkle,
      directLink: "/apps",
      items: [
        {
          title: "Apps",
          url: "/apps",
          icon: AppSparkle,
        },
      ],
    },
    {
      title: "Accounts",
      icon: Building2,
      items: [
        {
          title: "Account Explorer",
          url: "/accounts/explorer",
          icon: Search,
        },
        {
          title: "Account List View",
          url: "/accounts/list",
          icon: List,
        },
      ],
    },
    {
      title: "Opportunities",
      icon: Briefcase,
      items: [
        {
          title: "Opportunity List View",
          url: "/opportunities/list",
          icon: List,
        },
      ],
    },
    {
      title: "People",
      icon: UserIcon,
      items: [
        {
          title: "Person Explorer",
          url: "/persons/explorer",
          icon: Search,
        },
        {
          title: "Person List View",
          url: "/persons/list",
          icon: List,
        },
      ],
    },
    {
      title: "Deep Research",
      icon: Beaker,
      items: [
        {
          title: "Dataset List",
          url: "/deep-research",
          icon: List,
        },
        {
          title: "AI Fields",
          url: "/ai-fields",
          icon: Beaker,
        },
      ],
    },
    {
      title: "Campaigns",
      icon: Megaphone,
      items: [
        {
          title: "Campaign Report Card",
          url: "/campaigns/explorer",
          icon: Search,
        },
        {
          title: "Campaign List View",
          url: "/campaigns/list",
          icon: List,
        },
      ],
    },
    {
      title: "Channels",
      icon: Globe,
      items: [
        {
          title: "Channel Report Card",
          url: "/channels/explorer",
          icon: Search,
        },
        {
          title: "Channel List View",
          url: "/channels/list",
          icon: List,
        },
      ],
    },
    {
      title: "Salespeople",
      icon: Users,
      items: [
        {
          title: "Rep Report Card",
          url: "/salespeople/explorer",
          icon: Search,
        },
        {
          title: "Rep List View",
          url: "/salespeople/list",
          icon: List,
        },
      ],
    },
    {
      title: "Website",
      icon: Monitor,
      items: [
        {
          title: "Web Page Report Card",
          url: "/webpages/explorer",
          icon: Search,
        },
        {
          title: "Web Page List View",
          url: "/webpages/list",
          icon: List,
        },
      ],
    },
    {
      title: "Custom Channels",
      icon: FileText,
      items: [
        {
          title: "Custom Report Card",
          url: "/custom-channels/explorer",
          icon: Search,
        },
        {
          title: "Custom List View",
          url: "/custom-channels/list",
          icon: List,
        },
        {
          title: "Rules",
          url: "/custom-channels/rules",
          icon: Book,
        },
      ],
    },
  ],
};

function getUserInitials(user: UserType | null): string {
  if (!user) return "U";
  if (user.first_name && user.last_name) {
    return `${user.first_name.charAt(0)}${user.last_name.charAt(
      0,
    )}`.toUpperCase();
  }
  return user.email.charAt(0).toUpperCase();
}

function LogoArea() {
  const { state, isMobile } = useSidebar();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { companies, switchCompany } = useCompanySwitcher();
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  // Find the current company from the companies list
  const currentCompany = companies?.find((company) => company.is_current);

  // Get the current company name or fallback to a default
  const companyName =
    currentCompany?.name || user?.company?.name || "Dashboard";
  const initial = companyName.charAt(0).toUpperCase();

  if (state === "collapsed" && !isMobile) {
    return (
      <SidebarMenu className="h-16">
        <div className="flex items-center justify-center h-full">
          <SidebarMenuItem>
            <div className="relative w-8 h-8 group/logo">
              <div className="absolute inset-0 flex items-center justify-center rounded-lg corner-smooth-lg shadow-sm transition-colors bg-signal">
                <span className="text-lg font-bold text-white">{initial}</span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/logo:opacity-100 transition-opacity">
                <SidebarTrigger />
              </div>
            </div>
          </SidebarMenuItem>
        </div>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu className="h-16">
      <div className="flex items-center justify-between h-full">
        <SidebarMenuItem className="flex-none">
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger className="flex items-center gap-2 p-2 rounded-lg corner-smooth-lg hover:bg-muted transition-colors cursor-pointer">
              <div className="rounded-lg corner-smooth-lg shadow-sm w-8 h-8 flex items-center justify-center transition-colors bg-signal">
                <span className="text-lg font-bold text-white">{initial}</span>
              </div>
              <span className="font-medium">{companyName}</span>
              <ChevronDown className="size-4 text-muted-foreground" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="start"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-signal border-0">
                    <span className="text-sm font-bold text-white">
                      {initial}
                    </span>
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-bold">{companyName}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link to="/settings/company">
                    <Building2 className="mr-2 size-4" />
                    Company Settings
                  </Link>
                </DropdownMenuItem>
                {companies && companies.length > 1 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="flex items-center w-full">
                      <ArrowLeftRight className="mr-2 size-4" />
                      <span>Switch Company</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-56">
                      {companies.map((company) => (
                        <DropdownMenuItem
                          key={company.id}
                          onClick={() => switchCompany(company.id)}
                          disabled={company.is_current}
                          className="flex items-center gap-2"
                        >
                          {company.is_current && (
                            <Check className="size-4 text-foreground" />
                          )}
                          <span
                            className={
                              company.is_current ? "font-semibold" : ""
                            }
                          >
                            {company.name}
                          </span>
                          <span className="ml-auto text-xs text-muted-foreground">
                            {company.role}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <div className="w-8 h-8 rounded-lg corner-smooth-lg bg-muted flex items-center justify-center">
                    <span className="text-sm font-bold">
                      {getUserInitials(user)}
                    </span>
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-bold">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link to="/settings/user">
                    <UserIcon className="mr-2 size-4" />
                    User Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="text-destructive hover:text-destructive"
                >
                  <LogOut className="mr-2 size-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <div className="px-2 py-1.5">
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    Theme
                  </div>
                  <ToggleGroup
                    type="single"
                    value={theme}
                    onValueChange={(value) => {
                      if (value) {
                        setTheme(value as "system" | "light" | "dark");
                        setDropdownOpen(false);
                      }
                    }}
                    className="grid grid-cols-3 gap-1"
                  >
                    <ToggleGroupItem
                      value="system"
                      aria-label="System theme"
                      className="flex flex-col items-center gap-1 px-2 py-1.5 h-auto"
                    >
                      <Monitor className="size-4" />
                      <span className="text-xs">System</span>
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="light"
                      aria-label="Light theme"
                      className="flex flex-col items-center gap-1 px-2 py-1.5 h-auto"
                    >
                      <Sun className="size-4" />
                      <span className="text-xs">Light</span>
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="dark"
                      aria-label="Dark theme"
                      className="flex flex-col items-center gap-1 px-2 py-1.5 h-auto"
                    >
                      <Moon className="size-4" />
                      <span className="text-xs">Dark</span>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
        {!isMobile && (
          <div className="flex items-center h-full pr-2">
            <SidebarTrigger />
          </div>
        )}
      </div>
    </SidebarMenu>
  );
}

function UpsideLogo() {
  const { effectiveTheme } = useTheme();
  const { state } = useSidebar();

  // Use icon when collapsed, full logo when expanded
  const logoSrc =
    state === "collapsed"
      ? effectiveTheme === "dark"
        ? "/upside-icon-dark.svg"
        : "/upside-icon-light.svg"
      : effectiveTheme === "dark"
        ? "/upside-logo-dark.svg"
        : "/upside-logo-light.svg";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Link
          to="/home"
          className="relative pb-2 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform duration-150 ease-out active:scale-95 active:opacity-75"
        >
          <img
            src={logoSrc}
            alt="Upside"
            className={
              state === "collapsed"
                ? "h-[48px] w-[48px]"
                : "h-[72px] w-[120px] mr-2"
            }
            draggable={false}
          />
        </Link>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { state } = useSidebar();
  const deepResearchEnabled = useDeepResearchEnabled();
  const customChannelsVisible = useCustomChannelsVisibility();
  const customChannelsRulesManagementEnabled =
    useCustomChannelsRulesManagementEnabled();
  const miniAppsVisible = useMiniAppsVisibility();
  const { isAdmin } = usePermissions();

  // Filter nav items based on feature flags and permissions
  const navItems = NAV_CONFIG.navMain
    .filter((item) => item.title !== "Deep Research" || deepResearchEnabled)
    .filter((item) => item.title !== "Apps" || miniAppsVisible)
    .filter((item) => item.title !== "Custom Channels" || customChannelsVisible)
    .map((item) => {
      // Filter AI Fields submenu item for non-admins
      if (item.title === "Deep Research" && deepResearchEnabled) {
        return {
          ...item,
          items: item.items.filter(
            (subItem) => subItem.title !== "AI Fields" || isAdmin,
          ),
        };
      }
      // Filter Rules submenu item for Custom Channels based on feature flag and admin permission
      if (item.title === "Custom Channels") {
        return {
          ...item,
          items: item.items.filter(
            (subItem) =>
              subItem.title !== "Rules" ||
              (customChannelsRulesManagementEnabled && isAdmin),
          ),
        };
      }
      return item;
    });

  // Helper function to check if a navigation item is active
  const isNavItemActive = (url: string): boolean => {
    // Special case for root path
    if (url === "/") {
      // Only consider root active if we're exactly at root, not on any sub-path
      return location.pathname === "/";
    }

    // Special case for home page
    if (
      url === "/home" &&
      (location.pathname === "/" || location.pathname === "/home")
    ) {
      return true;
    }

    // Special case for dynamic explorer routes for campaign, channel, account, salesperson, webpage, and custom-channels explorer
    if (
      (url === "/campaigns/explorer" &&
        /^\/campaigns\/[^/]+\/explorer$/.test(location.pathname)) ||
      (url === "/channels/explorer" &&
        /^\/channels\/[^/]+\/explorer$/.test(location.pathname)) ||
      (url === "/accounts/explorer" &&
        (/^\/accounts\/[^/]+\/explorer$/.test(location.pathname) ||
          /^\/accounts\/[^/]+\/milestone-analyses$/.test(location.pathname))) ||
      (url === "/salespeople/explorer" &&
        /^\/salespeople\/[^/]+\/explorer$/.test(location.pathname)) ||
      (url === "/webpages/explorer" &&
        /^\/webpages\/[^/]+\/explorer$/.test(location.pathname)) ||
      (url === "/custom-channels/explorer" &&
        /^\/custom-channels\/[^/]+\/explorer$/.test(location.pathname))
    ) {
      return true;
    }

    // For other paths, check if the current path matches exactly or is a direct child
    if (url !== "/") {
      // Exact match
      if (location.pathname === url) {
        return true;
      }

      // Direct child path (e.g. /campaigns/123 is a child of /campaigns)
      if (location.pathname.startsWith(`${url}/`)) {
        return true;
      }

      // URL with query parameters
      if (location.pathname.includes(`${url}?`)) {
        return true;
      }

      return false;
    }

    return false;
  };

  // Helper to check if any sub-item in a section is active
  const isSectionActive = (item: NavItem): boolean => {
    // If it's a direct link section, check if that link is active
    if (item.directLink) {
      return isNavItemActive(item.directLink);
    }
    // Otherwise check if any sub-items are active
    return item.items.some((subItem) => isNavItemActive(subItem.url));
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <LogoArea />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map((item) => {
              const sectionActive = isSectionActive(item);
              const isDirectLink = !!item.directLink;

              return (
                <SidebarMenuItem key={item.title}>
                  {/* Render section header - clickable if directLink is provided */}
                  <SidebarMenuButton
                    tooltip={item.title}
                    isActive={isDirectLink && sectionActive}
                    className={cn(
                      "rounded-lg corner-smooth-lg",
                      isDirectLink
                        ? "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                        : "text-muted-foreground pointer-events-none",
                    )}
                    asChild={isDirectLink}
                  >
                    {isDirectLink && item.directLink ? (
                      <Link
                        to={item.directLink}
                        className={cn(
                          "flex items-center gap-3",
                          sectionActive
                            ? "hover:bg-muted/50 text-foreground font-medium"
                            : "hover:bg-muted/50 text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {item.icon && (
                          <item.icon
                            className={cn(
                              "size-4",
                              sectionActive
                                ? "text-foreground"
                                : "text-muted-foreground",
                            )}
                          />
                        )}
                        <span className="text-xs font-medium tracking-wider">
                          {item.title}
                        </span>
                      </Link>
                    ) : (
                      <div className="flex items-center gap-3">
                        {item.icon && (
                          <item.icon
                            className={cn(
                              "size-4",
                              sectionActive
                                ? "text-foreground"
                                : "text-muted-foreground",
                            )}
                          />
                        )}
                        <span className="text-xs font-medium tracking-wider">
                          {item.title}
                        </span>
                      </div>
                    )}
                  </SidebarMenuButton>

                  {/* Render sub-items if not a direct link or in collapsed mode (for icons) */}
                  {(!isDirectLink || state === "collapsed") && (
                    <SidebarMenuSub className="group-data-[collapsible=icon]:mx-0 group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-1 group-data-[collapsible=icon]:!block">
                      {item.items.map((subItem) => {
                        // For direct links in collapsed mode, only show items that aren't duplicating the section
                        if (
                          isDirectLink &&
                          state === "collapsed" &&
                          item.directLink === subItem.url
                        ) {
                          return null;
                        }

                        // For direct links in collapsed mode, we should respect the section active state
                        const isActive =
                          isDirectLink && state === "collapsed"
                            ? sectionActive
                            : isNavItemActive(subItem.url);
                        return (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={isActive}
                              size="md"
                              tooltip={subItem.title}
                              className={cn(
                                isActive
                                  ? "bg-accent text-accent-foreground font-medium"
                                  : "hover:bg-muted/50 text-muted-foreground hover:text-foreground",
                                "group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!p-2",
                              )}
                            >
                              <Link
                                to={subItem.url}
                                className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center"
                              >
                                {subItem.icon && (
                                  <subItem.icon className="size-4" />
                                )}
                                <span className="group-data-[collapsible=icon]:hidden">
                                  {subItem.title}
                                </span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  )}
                  {/* Add separator after Home section */}
                  {item.title === "Home" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Accounts section */}
                  {item.title === "Accounts" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Opportunities section */}
                  {item.title === "Opportunities" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Campaigns section */}
                  {item.title === "Campaigns" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Channels section */}
                  {item.title === "Channels" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Salespeople section */}
                  {item.title === "Salespeople" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Website section */}
                  {item.title === "Website" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Apps section */}
                  {item.title === "Apps" && state === "collapsed" && (
                    <SidebarSeparator className="my-1" />
                  )}
                  {/* Add separator after Custom Channels section */}
                  {item.title === "Custom Channels" &&
                    state === "collapsed" && (
                      <SidebarSeparator className="my-1" />
                    )}
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarSeparator className="my-2" />
        <SidebarMenu className="mb-2">
          <SidebarMenuItem>
            <SidebarMenuSubButton
              tooltip="Documentation"
              size="md"
              className="hover:bg-muted/50 text-muted-foreground hover:text-foreground group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!p-2"
              asChild
            >
              <a
                href="https://upsidelabs.notion.site/documentation"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center"
              >
                <Book className="size-4" />
                <span className="group-data-[collapsible=icon]:hidden">
                  Documentation
                  <ExternalLink className="ml-2 inline size-3 align-middle relative -top-[0.5px]" />
                </span>
              </a>
            </SidebarMenuSubButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <UpsideLogo />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
